import { Injectable } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { ErrorStreamChange } from '../models/$error.model';
import { Store } from '@ngrx/store';
import { Label } from '../../../globals/store/events.data';
import { environment } from '../../environments/environment';
import { doPrint, makeDefaultLabels } from '../helpers/helpers';
import { uiActions, UiMessage } from '../store/ui.era';
import { PROGRAM_NAMES } from '../../../globals/store/programs.data';
import { eventsFeature } from '../store/events.era';

export const labelHexColorsToNames = {
  '#54DDC7': 'Seafoam Green', '#FFD157': 'Golden Yellow', '#FF8A5C': 'Apricot', '#F05454': 'Grapefruit', '#8270DB': 'Lilac',
  '#A6DBFF': 'Baby Blue', '#0095A8': 'Teal', '#0099FF': 'Blue', '#C0D900': 'Lime Green', '#F09400': 'Carrot', '#FAD6FC': 'Baby Pink',
  '#dddddd': 'Light Grey', '#000000': 'Black', '#2E3887': 'Midnight Blue', '#13955E': 'Emerald Green', '#CC0000': 'Vermillion',
  '#EA80FC': 'Pink', '#999999': 'Mid Grey'
}
export type LabelType = 'event' | 'sponsor' | 'program' | 'session' | 'activity' | 'asset' | 'task' | 'performer' | 'publish' | 'exhibitor' | 'resource' | 'attendee'

@Injectable({ providedIn: 'root' })
export class UiService {

    navBar: any // NavBarComponent (Can't import because of share pages accessing this) 
    public version = '2.2.4';
    private mobileView: boolean // don't use this in components directly, need to use store for updates to handle properly
    public NEW_ID = 'new id';
    public EVENT_ID_SET = 'event_id_set';
    public EVENT_NO_ID = '_none_';
    public LANE_OFFSET_COMPLETE = 'lane_offset_complete';
    public MOUSE_RESUMED_MOVEMENT = 'mouse_resumed_movement';
    public ENGAGE_ALERT = 'engage_alert';
    public ZOOM_CHANGE = 'zoom_change';
    public LABELS_UPDATE = 'labels_update';
    public KICK_OUT = 'kick_out';
    public UPDATE_FROM_DB = 'update_from_db';
    public FRESH_VERSION = 'fresh_version';
    public CONTACT_ADDED = 'contact_added';
    public beta_period_expired = true;
    public trial_days_remaining;
    public months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    public programTerm = PROGRAM_NAMES;

    readonly share_base_url = environment.joi.shareBase + 'profile/';
    public expired_accounts = { trial: [], paid: [], hide_trial: false, hide_paid: false };
    // public LANE_CREATION_COMPLETE = 'lane_creation_complete';
    // public LANE_CREATION_START = 'lane_creation_start';
    public eventId;
    public zoom_amount = 0;
    public zoom_mulitplier = 1;
    public zoom_compensator = 100;
    public side_menu_collapse = false;
    public $errors: Subject<ErrorStreamChange> = new Subject();


    public stretchy_archive = {};
    public stretchy_archive_length = 0;
    public engage_status = '';

    public lockable_element: any = {};

    public eventTitle: string;
    weird_offset_matrix = {
        '0': 0,
        '20': 9,
        '40': 15,
        '60': 18,
        '80': 15,
        '100': 0,
    }
    public readonly labelTemplate = [{ id: 'custom01', color: '#54DDC7' }, { id: 'custom02', color: '#FFD157' }, { id: 'custom03', color: '#FF8A5C' }, { id: 'custom04', color: '#F05454' },
    { id: 'custom05', color: '#8270DB' }, { id: 'custom06', color: '#A6DBFF' }, { id: 'custom11', color: '#0095A8' }, { id: 'custom12', color: '#0099FF' },
    { id: 'custom13', color: '#C0D900' }, { id: 'custom14', color: '#F09400' }, { id: 'custom15', color: '#FAD6FC' }, { id: 'custom16', color: '#dddddd' },
    { id: 'custom17', color: '#000000' }, { id: 'custom18', color: '#2E3887' }, { id: 'custom19', color: '#13955E' }, { id: 'custom20', color: '#CC0000' },
    { id: 'custom21', color: '#EA80FC' }, { id: 'custom22', color: '#999999' }];

    public readonly labelOrder = [...this.defaultLabels.map(l => l.color), ...this.labelTemplate.map(l => l.color)]

    public readonly defaultLabelsDict = Object.assign({}, ...this.defaultLabels.map(item => ({ [item.name]: item })))
    public readonly defaultLabelsObj = Object.assign({}, ...this.defaultLabels.map(item => ({ [item.id]: item })))
    public readonly labelsByType: { [l in LabelType]: any } = {
      event: { title: "Event", ...this.defaultLabelsDict.Green },
      sponsor: { title: "Sponsor", color: '#41474f'}, // ...this.defaultLabelsDict.Green 
      program: { title: "Program", ...this.defaultLabelsDict.Purple },
      session: { title: "Session", ...this.defaultLabelsDict.Blue },
      activity: { title: "Activity", ...this.defaultLabelsDict.Yellow },
      asset: { title: "Asset", color: '#FF8A5C' },
      task: { title: "Task", color: '#41474f'},
      performer: { title: "Performer", color: '#41474f' }, // '#1A9BFC'
      publish: { title: 'Publish', color: '#8270DB' },
      exhibitor: { title: "Exhibitor", color: '#41474f'},
      resource: { title: "Resource", color: '#41474f'},
      attendee: { title: "Attendee", color: '#41474f' },
    }



    readonly engage_settings = { tasks: { filter: true, menu: true }, notices: {}, workforce: {} };
 
    public openMobileMenu() {
      // console.log("Should be opening menu")
      this.navBar.mobile_menu = true
    }

    // tslint:disable-next-line:member-ordering
    public static makeDefaultLabels() { 
      return makeDefaultLabels()
    }
    get defaultLabels() { 
      return UiService.makeDefaultLabels() 
    }

    // loadHubSpotScript() {
    //   if (document.getElementById('hs-script-loader')) return; // already loaded
    //   console.log("Loading HubSpot script")
    //   const script = document.createElement('script');
    //   script.src = '//js.hs-scripts.com/5389409.js';
    //   script.type = 'text/javascript';
    //   script.id = 'hs-script-loader';
    //   script.async = true;
    //   script.defer = true;
    //   document.getElementsByTagName('head')[0].appendChild(script);
    // }

    sortLabels(a: Label, b: Label) {
      const aColorIndex = this.labelOrder.indexOf(a.color)
      const bColorIndex = this.labelOrder.indexOf(b.color)
      if (aColorIndex !== bColorIndex) return aColorIndex - bColorIndex
      const aName = a.label.toLocaleUpperCase()
      const bName = b.label.toLocaleUpperCase()
      return aName < bName ? -1 : 1
    }
    constructor(public store: Store) {
      this.store.select(eventsFeature.selectEvent).subscribe(event => {
        this.eventTitle = event ? event.title : null
      })
      this.setMobileView(true)
      fromEvent(window, 'resize').subscribe(evt => this.setMobileView()) // this gets called hundreds or thousands of times during a single resizing of the window
    }

    setMobileView(force = false) {
      const isMobileView = window.innerWidth <= 768
      // console.log("Set mobile view called", { isMobileView })
      if (force || isMobileView !== this.mobileView) { // can fire many thousands of times per second so only do something when changes
        this.mobileView = isMobileView
        this.store.dispatch(uiActions.setMobileView(isMobileView))
      }
    }

    addUiMessage(message:UiMessage, unique_id?:string){ // called by log service to fix circular dependencies
      this.store.dispatch(uiActions.addUIMessage(message, unique_id))
    }

    changePageTitle(title: string, includeEvent: boolean = false) {
      // console.log({ title, includeEvent })
      document.title = `${title}` + (!includeEvent || !this.eventTitle ? '' : ` | ${this.eventTitle}`)
    }

    hasClass(el, className: string) {
        if (el.classList)
            return el.classList.contains(className)
        else
            return !!el.className.match(new RegExp('(\\s|^)' + className + '(\\s|$)'))
    }
    calcZoom(data) {
        const user_input = parseInt(data.zoomAmount, 10);
        this.zoom_mulitplier = 1 - (user_input / 100 * .5);
        this.zoom_compensator = 100 + user_input - this.weird_offset_matrix[user_input];
    }

    copy(x) {
        return JSON.parse(JSON.stringify(x));
    }

    addClass(el, className) {
        if (el.classList)
            el.classList.add(className)
        else if (!this.hasClass(el, className)) el.className += " " + className
    }

    removeClass(el, className) {
        if (el.classList)
            el.classList.remove(className)
        else if (this.hasClass(el, className)) {
            const reg = new RegExp('(\\s|^)' + className + '(\\s|$)')
            el.className = el.className.replace(reg, ' ')
        }
    }
    jsUcfirst(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }
    strWithBreak(str) {
        if (!str) return '';
        return str.replace(/(?:\r\n|\r|\n)/g, '<br>');
    }

    zoomClick(dir) {
        this.zoom_amount += dir * 20;
        if (this.zoom_amount > 100) {
            this.zoom_amount = 100;
            return;
        }
        if (this.zoom_amount < 0) {
            this.zoom_amount = 0;
            return;
        }
        this.updateZoomAmount(this.zoom_amount);
    }

    updateZoomAmount(zoomAmount, from_init?) {
      this.calcZoom({ zoomAmount, from_init })
    }

    print() {
      doPrint()
    }

    openInNewTab(url:string){
      window.open(url, '_blank')
    }
}


